import dayjs from 'dayjs';
import moment from 'moment';

import { SortValueType, TaskDateKey } from '@ge/models/constants';
import { Placeholders } from '@ge/models/constants';
import { DateTimeFormats } from '@ge/models/constants';
import { filterByView } from '@ge/shared/util';
import { getDelayDuration, isActiveDelays } from '@ge/shared/util/time-date';
import { sorter } from '@ge/util/metric-sorter';

import { getFilteredTasksTrackerData, getSelectedTaskTrackerData } from './tasks-tracker-util';

export const updateTimezoneTaskFields = (hydratedTasks, sites) => {
  const dateCache = (date, timezone) => dayjs(date).tz(timezone);
  const taskDates = Object.entries(hydratedTasks).map(([id, task]) => {
    const { site } = task;
    const { timezone } = sites[site?.id] ?? {};
    const taskDateInfo = { id };
    if (timezone) {
      taskDateInfo[TaskDateKey.COMPLETED_DATE_TIME_TZ] =
        task[TaskDateKey.COMPLETED_DATE_TIME] &&
        dateCache(task[TaskDateKey.COMPLETED_DATE_TIME], timezone);
      taskDateInfo[TaskDateKey.CREATE_DATE_TZ] =
        task[TaskDateKey.CREATE_DATE] && dateCache(task[TaskDateKey.CREATE_DATE], timezone);
      taskDateInfo[TaskDateKey.DUE_DATE_TZ] =
        task[TaskDateKey.DUE_DATE] && dateCache(task[TaskDateKey.DUE_DATE], timezone);
      taskDateInfo[TaskDateKey.ELIGIBLE_START_DATE_TZ] =
        task[TaskDateKey.ELIGIBLE_START_DATE] &&
        dateCache(task[TaskDateKey.ELIGIBLE_START_DATE], timezone);
      taskDateInfo[TaskDateKey.SCHEDULE_DATE_TZ] =
        task[TaskDateKey.SCHEDULE_DATE] && dateCache(task[TaskDateKey.SCHEDULE_DATE], timezone);
      taskDateInfo[TaskDateKey.START_DATE_TIME_TZ] =
        task[TaskDateKey.START_DATE_TIME] && dateCache(task[TaskDateKey.START_DATE_TIME], timezone);
      taskDateInfo[TaskDateKey.COMMITTED_DATE_TZ] =
        task[TaskDateKey.COMMITTED_DATE] && dateCache(task[TaskDateKey.COMMITTED_DATE], timezone);
      taskDateInfo[TaskDateKey.SCHEDULE_START_DATE_TZ] =
        task[TaskDateKey.SCHEDULE_START_DATE] &&
        dateCache(task[TaskDateKey.SCHEDULE_START_DATE], timezone);
      taskDateInfo[TaskDateKey.SCHEDULE_END_DATE_TZ] =
        task[TaskDateKey.SCHEDULE_END_DATE] &&
        dateCache(task[TaskDateKey.SCHEDULE_END_DATE], timezone);
    }
    return taskDateInfo;
  });
  taskDates.forEach((taskDateInfo) => {
    const task = hydratedTasks[taskDateInfo.id];
    task[TaskDateKey.COMPLETED_DATE_TIME_TZ] = taskDateInfo[TaskDateKey.COMPLETED_DATE_TIME_TZ];
    task[TaskDateKey.CREATE_DATE_TZ] = taskDateInfo[TaskDateKey.CREATE_DATE_TZ];
    task[TaskDateKey.DUE_DATE_TZ] = taskDateInfo[TaskDateKey.DUE_DATE_TZ];
    task[TaskDateKey.ELIGIBLE_START_DATE_TZ] = taskDateInfo[TaskDateKey.ELIGIBLE_START_DATE_TZ];
    task[TaskDateKey.SCHEDULE_DATE_TZ] = taskDateInfo[TaskDateKey.SCHEDULE_DATE_TZ];
    task[TaskDateKey.START_DATE_TIME_TZ] = taskDateInfo[TaskDateKey.START_DATE_TIME_TZ];
    task[TaskDateKey.COMMITTED_DATE_TZ] = taskDateInfo[TaskDateKey.COMMITTED_DATE_TZ];
    task[TaskDateKey.SCHEDULE_START_DATE_TZ] = taskDateInfo[TaskDateKey.SCHEDULE_START_DATE_TZ];
    task[TaskDateKey.SCHEDULE_END_DATE_TZ] = taskDateInfo[TaskDateKey.SCHEDULE_END_DATE_TZ];
  });
};

export const hydrateTask = (task, { assets, sites, technicians }) => {
  const hydratedTask = { ...task };
  if (task.asset?.id && assets[task.asset.id]) {
    const taskAsset = assets[task.asset.id];
    hydratedTask.asset = {
      id: taskAsset.id,
      name: taskAsset.name,
      state: taskAsset.metrics?.state,
      // TODO - we need to get this from some association
      events: 4,
      anomalies: 2,
      tasks: 4,
      openEvents: 1,
      openAnomalies: 2,
    };
  }
  if (task.site && sites[task.site.id]) {
    const taskSite = sites[task.site.id];
    hydratedTask.site = {
      id: taskSite.id,
      name: taskSite.name,
      timezone: taskSite.timezone,
    };
  }
  if (task.crew) {
    hydratedTask.crew = Object.values(technicians)
      .filter((tech) => tech.crew && tech.crew.id === task.crew.id)
      .map((tech) => {
        const { crew, ...rest } = tech;
        return rest;
      });
  }
  if (task.specialist && technicians[task.specialist.id]) {
    hydratedTask.specialist = technicians[task.specialist.id];
  }
  if (task?.rdspp) {
    hydratedTask.rdspp = task?.rdspp ?? '';
  }
  if (task?.delayDetails?.length) {
    const data = task.delayDetails;
    let total_delay_days = 0;
    data.map((e) => {
      const duration = getDelayDuration(null, e, true) || 0;
      total_delay_days += duration;
    });
    hydratedTask.delayDuration = total_delay_days ? total_delay_days : null;
    hydratedTask.delay = data.length;
  }

  if (task?.multiDaySchedule?.schedules) {
    const data = task?.multiDaySchedule?.schedules;
    let moments = data?.map((d) => moment(d?.scheduleStartDate)),
      minDate = new Date(moment.min(moments)),
      min = dayjs(minDate).format(DateTimeFormats.DEFAULT_DATE);
    min !== 'Invalid Date' ? min : Placeholders.DASH;
    hydratedTask.scheduleStartDate = min;
  }

  if (task?.multiDaySchedule?.schedules) {
    const data = task?.multiDaySchedule?.schedules;
    let moments = data?.map((d) => moment(d?.scheduleEndDate)),
      maxDate = new Date(moment.max(moments)),
      max = dayjs(maxDate).format(DateTimeFormats.DEFAULT_DATE);
    max !== 'Invalid Date' ? max : Placeholders.DASH;
    hydratedTask.scheduleEndDate = max;
  }

  return hydratedTask;
};

export const getSortedTasks = (
  hydratedTasks,
  currentView,
  sortMetric,
  sortDirection,
  tasksTrackerData,
) => {
  let taskArray = hydratedTasks;

  if (currentView) {
    taskArray = filterByView(taskArray, currentView);
  }
  const sortedTasks = taskArray.sort(sorter(sortMetric, sortDirection, SortValueType.ALPHANUMERIC));
  const taskTrackerData = getSelectedTaskTrackerData(tasksTrackerData);
  return taskTrackerData && sortedTasks?.length
    ? getFilteredTasksTrackerData(sortedTasks, taskTrackerData)
    : sortedTasks;
};

export const getTaskById = (tasks, taskId) => tasks?.find((task) => taskId === task?.id);

export const filterDelayTask = (data) => {
  if (!data?.delayDetails?.length) return null;
  return data.delayDetails?.filter((e) => isActiveDelays(e.endDate));
};

const getBundleDelay = (bundledTasksList) => {
  if (!bundledTasksList) return null;
  let delay = [];
  bundledTasksList.map((bundleTask) => {
    let bundleDelay = filterDelayTask(bundleTask);
    bundleDelay?.length && delay.push(bundleDelay);
  });
  return delay;
};

export const getActiveDelays = (tasks) => {
  const delay = [];
  if (tasks?.length) {
    tasks.map((e) => {
      let delayDetails = filterDelayTask(e);
      delayDetails?.length && delay.push(delayDetails);
      let bundleDelay = getBundleDelay(e?.bundledTasksList);
      bundleDelay?.length && delay.push(bundleDelay);
    });
  } else {
    let delayDetails = filterDelayTask(tasks);
    delayDetails?.length && delay.push(delayDetails);
    let bundleDelay = getBundleDelay(tasks?.bundledTasksList);
    bundleDelay?.length && delay.push(bundleDelay);
  }
  return delay?.length;
};

export const getMultidaySchedule = (data) => {
  if (Object.keys(data?.multiDaySchedule || {}).length !== 0) {
    return data?.multiDaySchedule?.isMultiDay;
  }
};

export const getBundleMultiScheduleDay = (bundledTasksList) => {
  let multiDay = false;
  if (!bundledTasksList) return false;
  bundledTasksList.map((bundleTask) => {
    if (Object.keys(bundleTask?.multiDaySchedule || {}).length !== 0) {
      if (bundleTask?.multiDaySchedule?.isMultiDay) {
        multiDay = bundleTask?.multiDaySchedule?.isMultiDay;
      }
    }
  });
  return multiDay;
};

export const getMultiDaySchedule = (tasks) => {
  let multiDay = false;
  if (tasks?.length) {
    tasks.map((e) => {
      if (getMultidaySchedule(e)) {
        multiDay = getMultidaySchedule(e);
      }
      let bundledMultiDaySchedule = getBundleMultiScheduleDay(e?.bundledTasksList);
      if (bundledMultiDaySchedule) {
        multiDay = bundledMultiDaySchedule;
      }
    });
  } else {
    let multiDaySchedule = getMultidaySchedule(tasks);
    if (multiDaySchedule) {
      multiDay = multiDaySchedule;
    }
    let bundledMultiDaySchedule = getBundleMultiScheduleDay(tasks?.bundledTasksList);
    if (bundledMultiDaySchedule) {
      multiDay = bundledMultiDaySchedule;
    }
  }
  return multiDay;
};

export const multidayTaskSchedules = (tasks, crew, state = false) => {
  return tasks?.multiDaySchedule?.schedules?.filter((e, index) => {
    let individualDaySch = null;
    if (e?.isIndividualDay && e?.individualDays.length) {
      individualDaySch = e.individualDays?.filter((val) =>
        state ? !val.crewIds : crew?._id === val.crewIds,
      );
      if (individualDaySch?.length) {
        tasks.multiDaySchedule.schedules[index].individualDays = individualDaySch;
        return true;
      }
    } else return state ? !e.crewIds : crew?._id === e.crewIds;
  });
};

export const getFiterTasks = (data) =>
  data?.length
    ? data?.reduce((unique, o) => {
        if (!unique.some((obj) => obj.id === o.id)) unique.push(o);
        return unique;
      }, [])
    : [];
